import { Link2, RefreshCw, Unplug } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SocialConnection } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { accountHandle, type ConnectionState } from '../schemas/channel-state';

import { ChannelStep, StepBadge, StepRow } from './channel-step';

export interface ConnectionStepProps {
  state: ConnectionState;
  connection: SocialConnection | undefined;
  editable: boolean;
  connecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

/** Step 1: connect, reconnect when the tokens lapsed, disconnect with a confirmation. */
export function ConnectionStep({
  state,
  connection,
  editable,
  connecting,
  onConnect,
  onDisconnect,
}: ConnectionStepProps) {
  const { t } = useTranslation('social');
  const format = useFormat();

  const badge =
    state === 'connected' ? (
      <StepBadge tone="done">{t('status.connected')}</StepBadge>
    ) : state === 'disconnected' ? (
      <StepBadge tone="todo">{t('status.disconnected')}</StepBadge>
    ) : (
      <StepBadge tone="attention">{t(`status.${state}`)}</StepBadge>
    );

  const connectButton = (label: string, icon: typeof Link2) => {
    const Icon = icon;
    return (
      <Button type="button" size="sm" disabled={connecting} onClick={onConnect}>
        <Icon aria-hidden />
        {connecting ? t('connection.connecting') : label}
      </Button>
    );
  };

  return (
    <ChannelStep index={1} title={t('connection.title')} badge={badge}>
      {state === 'disconnected' ? (
        <>
          <p className="text-muted-foreground text-sm">{t('connection.disconnected')}</p>
          <p className="text-muted-foreground text-xs">{t('connection.businessOnly')}</p>
          {editable ? <div>{connectButton(t('connection.connect'), Link2)}</div> : null}
        </>
      ) : (
        <>
          {state !== 'connected' ? (
            <Alert variant="destructive" role="alert">
              <AlertTitle>
                {t(state === 'expired' ? 'connection.expiredTitle' : 'connection.errorTitle')}
              </AlertTitle>
              <AlertDescription>
                {state === 'expired' ? t('connection.expired') : null}
                {connection?.lastError ? (
                  <span className="block text-xs">
                    {t('connection.lastError')}: {connection.lastError}
                  </span>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          {connection ? (
            <dl className="grid gap-3 sm:grid-cols-2">
              <StepRow label={t('connection.account')}>
                <span className="font-medium">{accountHandle(connection)}</span>
              </StepRow>
              <StepRow label={t('connection.accountType')}>{connection.accountType ?? '—'}</StepRow>
              <StepRow label={t('connection.connectedAt')}>
                {format.dateTime(connection.connectedAt)}
              </StepRow>
              <StepRow label={t('connection.accessUntil')}>
                {connection.accessExpiresAt ? format.dateTime(connection.accessExpiresAt) : '—'}
              </StepRow>
            </dl>
          ) : null}
          {editable ? (
            <div className="flex flex-wrap gap-2">
              {state !== 'connected' ? connectButton(t('connection.reconnect'), RefreshCw) : null}
              <Button type="button" variant="outline" size="sm" onClick={onDisconnect}>
                <Unplug aria-hidden />
                {t('connection.disconnect')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </ChannelStep>
  );
}
